<template>

    <div>
        <v-card flat :color="settings.surveyBoxColor" v-if="activeQuestion || showPassword">
            <v-card-text>

                <!-- PROGRESS BAR -->
                <v-progress-linear v-show="componentData.settings.show_progress"
                                   striped
                                   rounded
                                   height="20"
                                   :value="progress"
                                   :color="settings.actionButtonColor"
                ></v-progress-linear>

                <!-- DYNAMIC QUESTION COMPONENTS -->
                <component v-if="activeQuestion"
                           v-model="activeModel"
                           class="mt-5"
                           :component-data="componentData"
                           v-bind:is="ucFirst(activeQuestion.type) + 'Question'"
                           :variable="activeQuestion"
                           :translations="subPanelTranslations"
                           :next-button-text="subPanelTranslations.survey_next_button_text"
                           :back-button-text="subPanelTranslations.survey_back_button_text"
                           :skip-button-text="subPanelTranslations.survey_skip_button_text"
                           :back-button-enabled="index > 1"
                           :locale="locale"
                           :key="activeQuestion.uuid"
                           :background-color="settings.surveyBoxColor"
                           :button-color="settings.actionButtonColor"
                           :required="isAnswerRequired"
                           @input="saveAnswer"
                           @back="backClicked"
                           @skip="skipClicked"
                           :error-messages="this.activeError"
                           :saving="saving"
                ></component>

                <!-- PASSWORD FIELD -->
                <password-question v-if="showPassword"
                                   :background-color="settings.surveyBoxColor"
                                   :button-color="settings.actionButtonColor"
                                   :translations="subPanelTranslations"
                                   :recaptcha-enabled="componentData.settings.use_recaptcha"
                                   :recaptcha-site-key="componentData.settings.recaptcha_site_key"
                                   :recaptcha-language="componentData.settings.recaptcha_language"
                                   :auto-focus="true"
                                   :saving="saving"
                                   @complete="passwordComplete"
                ></password-question>

            </v-card-text>
        </v-card>

        <div>

            <!-- START REGISTRATION SURVEY BUTTON EDIT MODE-->
            <div v-if="editMode">
                <text-editor
                    v-model="startRegistrationButtonTextModel"
                    hint="Start registration button text">

                    <div>
                        <v-btn
                            :dark="isDarkColor(settings.actionButtonColor)"
                            :color="getRgbaColor(settings.actionButtonColor)"
                        >
                            {{ startRegistrationButtonTextModel }}

                        </v-btn>
                    </div>

                </text-editor>
            </div>

            <!-- START REGISTRATION BUTTON RENDER MODE -->
            <div v-if="!editMode" class="d-flex justify-md-space-between justify-sm-center justify-center">
                <v-btn v-show="!activeQuestion && !completed"
                       :color="getRgbaColor(settings.actionButtonColor)"
                       :dark="isDarkColor(settings.actionButtonColor)"
                       :disabled="!fingerprint"
                       class="mk-md-0"
                       @click="beginSurvey">
                    {{ subPanelTranslations.start_registration_button_text }}
                </v-btn>
            </div>

        </div>

        <!-- BOTTOM SHEET FOR TEST DATA -->
        <v-dialog v-model="bottomSheetModel"
                  v-if="isTestMode">
            <test-data :test-data="componentData.testData" :fingerprint="fingerprint"/>
        </v-dialog>

        <json v-if="false" :data="answers"/>
    </div>
</template>

<script>

import CheckboxQuestion from "../DynamicProflingQuestions/CheckboxQuestion";
import DateQuestion from "../DynamicProflingQuestions/DateQuestion";
import EmailQuestion from "../DynamicProflingQuestions/EmailQuestion";
import KeywordQuestion from "../DynamicProflingQuestions/KeywordQuestion";
import LocaleQuestion from "../DynamicProflingQuestions/LocaleQuestion";
import NumberQuestion from "../DynamicProflingQuestions/NumberQuestion";
import RadioQuestion from "../DynamicProflingQuestions/RadioQuestion";
import TextQuestion from "../DynamicProflingQuestions/TextQuestion";
import PhoneQuestion from "../DynamicProflingQuestions/PhoneQuestion";
import colorMixin from "../DynamicProflingQuestions/colorMixin";
import PasswordQuestion from "../DynamicProflingQuestions/PasswordQuestion";
import TitleCardList from "../TitleCardList";
import LabelValue from "../LabelValue";
import TestData from "./TestData";
import TextEditor from "../TextEditor";

import Json from "vue-json-pretty";
import axios from "axios";
import {fingerprint} from "../../mixins/fingerprintMixin";
import {registrationQuestionsMixin} from "../../mixins/registrationQuestionsMixin";

export default {

    name: "RegistrationSurvey",

    mixins: [colorMixin, fingerprint, registrationQuestionsMixin],

    components: {
        Json,
        CheckboxQuestion,
        DateQuestion,
        EmailQuestion,
        KeywordQuestion,
        LocaleQuestion,
        NumberQuestion,
        RadioQuestion,
        TextQuestion,
        PasswordQuestion,
        PhoneQuestion,
        TitleCardList,
        LabelValue,
        TestData,
        TextEditor
    },

    props: {

        locale: {
            type: Object,
            required: true,
        },
        settings: {
            type: Object,
            required: true
        },
        componentData: {
            type: Object,
            required: true,
        },
        subPanelTranslations: {
            type: Object,
            required: true
        },
        editMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        subPanelId: {
            type: Number,
            required: false,
            default: null,
        },
        showTestData: {
            type: Boolean,
            required: false,
            default: null,
        }
    },

    data() {
        return {
            activeQuestion: null,
            activeError: null,
            activeModel: null,
            index: 0,
            started: false,
            completed: false,
            completedQuestions: 0,
            saving: false,

            // Emitted data
            answers: {},
            password: null,
            recaptcha: null,
            testDataButtonModel: true,
            fingerprint: null,
            eventCategory: null,
        }
    },

    mounted() {

        this.eventCategory = 'registration-' + this.subPanelId + '-' + this.locale.code;

        this.$nextTick(() => {
            if (window.Spark.googleAnalyticsUA) {
                this.$gtag.event('welcome', {
                    'event_category': this.eventCategory,
                    'event_label': 'welcome',
                });
            }
        });

        this.generateFingerprint(data => {
            this.fingerprint = data.result;
        });
    },

    computed: {

        startRegistrationButtonTextModel: {
            get() {
                return this.subPanelTranslations.start_registration_button_text;
            },
            set(value) {
                this.$emit('change:subPanelTranslations', {start_registration_button_text: value});
            }
        },

        bottomSheetModel: {
            get() {
                return this.showTestData;
            },
            set() {
                this.$emit('test-data-closed');
            }
        },

        progress() {
            return Math.round(this.completedQuestions / (this.componentData.questions.length + 1) * 100);
        },

        questionsRemaining() {
            return this.componentData.questions.length - this.completedQuestions;
        },

        showRecaptcha() {
            return this.questionsRemaining === 0 && this.componentData.settings.use_recaptcha;
        },

        showPassword() {
            return this.completed;
        },

        isTestMode() {
            return this.componentData.testData;
        },

        isAnswerRequired() {
            if (this.activeQuestion.dynamic_profiling_validators) {
                return !!this.activeQuestion.dynamic_profiling_validators.find(validator => validator.type === 'required');
            }
            return this.mandatoryQuestions.includes(this.activeQuestion.name);
        }
    },

    methods: {

        beginSurvey() {
            this.$emit('started');

            if (window.Spark.googleAnalyticsUA) {
                this.$gtag.event('start', {
                    'event_category': this.eventCategory,
                    'event_label': 'registration-survey-start',
                });
            }

            axios.put('/registration/start', {signature: this.fingerprint})
                .catch(error => {
                    throw new Error('Registration start failed to register...')
                });

            this.nextQuestion();
        },

        saveAnswer(answer) {

            if (this.activeQuestion.name === "EMAIL") {
                this.validateEmail(answer);
            } else {
                this.storeAnswerData(answer);
            }
        },

        storeAnswerData(answer) {
            if (!this.answers[this.activeQuestion.name]) {
                this.completedQuestions++;
            }

            this.answers[this.activeQuestion.name] = answer;
            this.answers = Object.assign({}, this.answers);

            this.nextQuestion();
        },

        validateEmail(answer) {

            this.saving = true;

            axios.post('/registration/validate-email', {email: answer})

                .then(({data}) => {

                    if (data.available) {
                        this.storeAnswerData(answer);
                    } else {
                        this.activeError = this.subPanelTranslations.survey_duplicate_email_error;
                    }
                })
                .catch(error => {

                    if (error.response.status === 429) {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Too many attempts! Wait a minute!'
                        });
                    } else {

                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'OOPS Network error has occurred!'
                        });
                    }
                })
                .finally(() => {
                    this.saving = false;
                });
        },

        backClicked() {

            if (this.index > 0) {
                this.index--;
                this.activeQuestion = Object.assign({}, this.componentData.questions[this.index - 1]);
                this.activeModel = this.answers[this.activeQuestion.name]
                this.activeError = null;
            }
        },

        skipClicked() {
            delete this.answers[this.activeQuestion.name];
            this.answers = Object.assign({}, this.answers);
            this.nextQuestion();
        },

        nextQuestion() {
            if (this.index < this.componentData.questions.length) {
                this.started = true;
                this.activeError = false;
                this.activeQuestion = this.componentData.questions[this.index];

                if (this.answers[this.activeQuestion.name]) {
                    this.activeModel = this.answers[this.activeQuestion.name];
                } else {
                    this.activeModel = null
                }
                this.index++;
            } else {

                this.activeQuestion = null;
                this.activeError = null;
                this.completed = true;
            }
        },

        passwordComplete(data) {
            this.password = data.password;
            this.captchaToken = data.captchaToken;

            this.$emit('completed');
            this.saveData();
        },

        saveData() {

            const data = {
                answers: this.answers,
                password: this.password,
                captchaToken: this.captchaToken,
                fingerprint: this.fingerprint
            };

            this.saving = true;

            axios.post('/registration/answers', data)

                .then(({data}) => {

                    if (window.Spark.googleAnalyticsUA) {
                        this.$gtag.event('complete', {
                            'event_category': this.eventCategory,
                            'event_label': 'complete',
                        });

                        this.$gtag.event('sign_up');
                    }

                    this.sendToUrl('/landing-page/' + this.subPanelId + '/' + this.locale.code + '/registration-complete');
                })
                .catch(error => {

                    console.log(error);

                    if (error.response.status === 429) {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Too many attempts! Wait a minute!'
                        });
                    } else if (error.response.status === 409) {

                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Email address has already been registered!'
                        });

                    } else {

                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'OOPS Network error has occurred!'
                        });
                    }

                    this.saving = false;

                });
        }
    }
}
</script>
